import { Component, OnInit, inject, signal, computed, effect, HostListener, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterLink, RouterLinkActive, RouterOutlet } from '@angular/router';
import { toSignal } from '@angular/core/rxjs-interop';
import { firstValueFrom, catchError, of } from 'rxjs';
import { TranslatePipe } from '@ngx-translate/core';
import { TeamService, JobTitle } from '../../core/services/team.service';
import { resolveAvatarStyle } from '../../core/utils/avatar-color.util';
import { LanguageService, AVAILABLE_UI_LANGUAGES } from '../../core/services/language.service';

import { AccountModalComponent } from '../../component/account-modal/account-modal.component';
import { AddUserModalComponent } from '../../component/add-user-modal/add-user-modal.component';
import { UserListModalComponent } from '../../component/user-list-modal/user-list-modal';
import { EstablishmentSwitcherComponent } from '../../component/establishment-switcher/establishment-switcher.component';
import { ToastComponent } from '../../component/toast/toast.component';
import { QrModalComponent } from '../../component/qr-modal.component';
import { SessionService } from '../../core/session.service';
import { AuthService } from '../../auth/auth.service';
import { WizardService, WizardStatusDto } from '../../core/services/wizard.service';
import { ToastService } from '../../core/services/toast.service';
import { SatisfactionService } from '../../core/services/satisfaction.service';
import { WizardModalHostComponent as WizardModalComponent } from '../../component/wizard-modal/wizard-modal-host.component';
import { SupportComponent } from '../../pages/support/support.component';
import { ActualiteService } from '../../core/services/actualite.service';
import { MaintenanceService } from '../../core/services/maintenance.service';
import { MaintenanceBannerComponent } from '../../component/maintenance-banner/maintenance-banner.component';
import { OrganisationsService } from '../../core/services/organisations.service';

type SavedStyle = {
  el: HTMLElement;
  overflow?: string;
  position?: string;
  top?: string;
  width?: string;
  paddingRight?: string;
};
let _saved: SavedStyle[] = [];
let _locked = false;
function getScrollbarWidth(): number { return window.innerWidth - document.documentElement.clientWidth; }
/**
 * `inertBackground` (défaut `true`) inertifie `#w-topbar`/`#w-main` -- correct pour une modale
 * déclarée au niveau racine de la coquille, SIBLING de `#w-main` (ex. app-account-modal,
 * app-wizard-modal dans shell.component.html : `inert` sur `#w-main` n'affecte alors jamais la
 * modale elle-même). Passer `false` pour toute modale déclarée à l'intérieur d'une page routée
 * (donc DESCENDANTE de `#w-main` via le router-outlet, ex. app-confirm-modal de site.component /
 * prevention-my-offers.component / offer-form-page.component) -- `inert` se propage à TOUT le
 * sous-arbre DOM sans exception possible pour un enfant (même `position:fixed`), donc inertifier
 * `#w-main` y rendrait la modale elle-même non cliquable (boutons Annuler/Confirmer morts). Le
 * blocage du scroll (partie au-dessus) reste, lui, toujours actif dans les deux cas. */
export function lockPageScroll(options: { inertBackground?: boolean } = {}): void {
  const { inertBackground = true } = options;
  if (_locked) return; _locked = true; _saved = [];
  const candidates: (HTMLElement | null)[] = [
    document.documentElement, document.body,
    document.getElementById('w-main'),
    document.getElementById('w-content'),
    document.querySelector('#w-content .inner') as HTMLElement | null,
  ];
  const sbw = getScrollbarWidth();
  candidates.forEach((el) => {
    if (!el) return;
    const style = getComputedStyle(el);
    const canScroll = el.scrollHeight > el.clientHeight && /(auto|scroll)/.test(style.overflowY || '');
    if (el === document.body || el === document.documentElement || canScroll) {
      _saved.push({
        el,
        overflow: el.style.overflow,
        position: el.style.position,
        top: el.style.top,
        width: el.style.width,
        paddingRight: el.style.paddingRight,
      });
      if (el === document.body) {
        const y = window.scrollY || document.documentElement.scrollTop || 0;
        (el as any).dataset['scrollLockY'] = String(y);
        el.style.top = `-${y}px`;
        el.style.position = 'fixed';
        el.style.width = '100%';
        el.style.overflow = 'hidden';
        if (sbw > 0) el.style.paddingRight = `${sbw}px`;
      } else {
        el.style.overflow = 'hidden';
        if (sbw > 0) el.style.paddingRight = `${sbw}px`;
      }
    }
  });
  if (inertBackground) {
    document.getElementById('w-topbar')?.setAttribute('inert', '');
    document.getElementById('w-main')?.setAttribute('inert', '');
  }
}
export function unlockPageScroll(): void {
  if (!_locked) return; _locked = false;
  _saved.forEach((s) => {
    const el = s.el;
    el.style.overflow = s.overflow || '';
    el.style.position = s.position || '';
    el.style.top = s.top || '';
    el.style.width = s.width || '';
    el.style.paddingRight = s.paddingRight || '';
    if (el === document.body) {
      const y = Number((el as any).dataset['scrollLockY'] || 0);
      delete (el as any).dataset['scrollLockY'];
      window.scrollTo(0, y);
    }
  });
  _saved = [];
  document.getElementById('w-topbar')?.removeAttribute('inert');
  document.getElementById('w-main')?.removeAttribute('inert');
}

@Component({
  selector: 'app-shell',
  standalone: true,
  imports: [
    CommonModule,
    RouterOutlet, RouterLink, RouterLinkActive,
    AccountModalComponent,
    AddUserModalComponent,
    UserListModalComponent,
    EstablishmentSwitcherComponent,
    WizardModalComponent,
    SupportComponent,
    ToastComponent,
    QrModalComponent,
    MaintenanceBannerComponent,
    TranslatePipe,
  ],
  templateUrl: './shell.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./shell.component.scss'],
})
export class ShellComponent implements OnInit {
  readonly session = inject(SessionService);
  readonly auth = inject(AuthService);
  readonly router = inject(Router);
  readonly wizardApi = inject(WizardService);
  readonly toastService = inject(ToastService);
  // DEPRECATED (2026-09-08) : exclusivement utilisé par checkQualityAlert() (plus bas), retiré
  // avec l'alerte qualité -- voir la note sur showActionRequise ci-dessous pour le détail complet.
  readonly satisfactionApi = inject(SatisfactionService);
  readonly actualiteApi = inject(ActualiteService);
  readonly languageService = inject(LanguageService);
  readonly availableLanguages = AVAILABLE_UI_LANGUAGES;
  private maintenanceService = inject(MaintenanceService);
  private readonly organisationsService = inject(OrganisationsService);
  private readonly teamSvc = inject(TeamService);

  /** Couleur d'avatar de l'utilisateur connecté, cohérente avec gestion-equipes/site/user-list-modal */
  private readonly jobTitles = toSignal(
    this.teamSvc.getJobTitles().pipe(catchError(() => of([] as JobTitle[]))),
    { initialValue: [] as JobTitle[] }
  );
  private readonly jobTitleColorMap = computed(() => new Map(this.jobTitles().map(j => [j.id, j.color])));
  private readonly currentUser = toSignal(this.auth.auth$, { initialValue: null });
  readonly currentUserAvatarStyle = computed(() => {
    const user = this.currentUser()?.user;
    if (!user) return {};
    return resolveAvatarStyle(
      { avatar_color: user.avatar_color, job_title_ids: user.job_title_ids },
      id => this.jobTitleColorMap().get(id)
    );
  });

  maintenanceBanner = signal<string | null>(null);
  private initialized = false;

  showAccountModal = signal(false);
  showWizardModal = signal(false);
  showSupportModal = signal(false);
  showAccountMenu = signal(false);
  showAddUserModal = signal(false);
  showUserListModal = signal(false);
  showQrModal = signal(false);

  showWizardCta = signal(false);
  wizardLabel = signal<string>('Configurer site');
  // DEPRECATED (2026-09-08) : décision produit de retirer l'alerte qualité, partout dans le front
  // (ce bouton top bar "Action requise" ET le bandeau de la page Satisfaction,
  // satisfaction.component.ts). checkQualityAlert() (plus bas) et son appel dans
  // _watchSelectedEstablishment ne sont plus invoqués -- signal et méthode conservés inatteignables
  // pour faciliter un retour arrière. Côté waitora-backend : endpoint GET
  // /api/v1/satisfaction/establishments/{id}/quality-alert (SatisfactionController.checkQualityAlert)
  // et PatientFeedbackRepository.countCriticalFeedbacks deviennent orphelins, de même que
  // hasActiveAction/getLastActionDate/getLastCompletedActionDate de CorrectiveActionService (plus
  // aucun appelant une fois cet endpoint retiré -- corrige le bandeau "à garder" posé lors de la
  // dépréciation des actions correctives).
  showActionRequise = signal(false);
  unreadMentionsCount = signal(0);

  sidebarPinned = signal(localStorage.getItem('waitora_sidebar_pinned') === 'true');

  toggleSidebarPin(): void {
    this.sidebarPinned.update(v => {
      const next = !v;
      localStorage.setItem('waitora_sidebar_pinned', String(next));
      return next;
    });
  }

  /** Sous-menu ouvert automatiquement tant qu'on est sur une des pages du groupe Pilotage multisites */
  get isPilotageSubmenuOpen(): boolean {
    return this.router.url.startsWith('/statistiques') || this.router.url.startsWith('/satisfaction') || this.router.url.startsWith('/fil-actualite') || this.router.url.startsWith('/comm-prevention') || this.isPreventionNavActive;
  }

  /** Surbrillance de la ligne parente : uniquement sur sa propre page, pour ne jamais avoir 2 onglets actifs en même temps */
  get isPilotageRowActive(): boolean {
    return this.router.url.startsWith('/statistiques');
  }

  /**
   * Surbrillance du lien "Prévention" : couvre `/pilotage-prevention` (vue d'ensemble groupe) ET tout
   * le périmètre Catalogue V2 (`/prevention-offres`, `/prevention-offres/bibliotheque`,
   * `/prevention-offres/bilans`, `/prevention-offres/:offerId[/sites]`) -- routes SŒURS à plat, jamais
   * imbriquées sous `/pilotage-prevention` (convention "routes plates" du repo, LOT9_0EEA), donc
   * `routerLinkActive` seul (matching exact du path) ne peut jamais les relier. Audit d'harmonisation
   * Prévention (2026-08-29) : sans ce getter, le lien perdait tout repère visuel dès qu'on quittait la
   * page Vue d'ensemble.
   */
  get isPreventionNavActive(): boolean {
    return this.router.url.startsWith('/pilotage-prevention') || this.router.url.startsWith('/prevention-offres');
  }

  toggleSubmenu(event: Event): void {
    event.stopPropagation();
    this.router.navigate(['/statistiques']);
  }

  get hasAccueilPermission() { return this.auth.hasPermission('ACCUEIL'); }
  get hasPrevisionsPermission() { return this.auth.hasPermission('PREVISIONS'); }
  get hasSatisfactionPermission() { return this.auth.hasPermission('SATISFACTION'); }
  get hasFilActualitePermission() { return this.auth.hasPermission('FIL_ACTUALITE'); }
  get hasPreventionPermission() { return this.auth.hasPermission('PREVENTION'); }
  get hasQualitePermission() { return this.auth.hasPermission('QUALITE'); }
  get hasActualitePermission() { return this.auth.hasPermission('ACTUALITE'); }
  get hasStatistiquesPermission() { return this.auth.hasPermission('STATISTIQUES'); }
  get hasEtablissementPermission() { return this.auth.hasPermission('ETABLISSEMENT'); }
  get hasKitCommunicationPermission() { return this.auth.hasPermission('KIT_COMMUNICATION'); }
  get hasUserDroitPermission() { return this.auth.hasPermission('USER_DROIT'); }
  get hasSupervisionPermission() { return this.auth.hasPermission('SUPERVISION'); }
  get hasEquipementsPermission() { return this.auth.hasPermission('EQUIPEMENTS'); }
  get hasPilotageMultiSitesPermission() { return this.auth.hasPermission('SUPERVISION'); }
  get hasApplicationMobilePermission() { return this.auth.hasPermission('APPLICATION_MOBILE'); }
  get isV2() { return this.auth.hasPermission('SUPERVISION'); }

  /** Logo + nom du groupe (organisation), affichés dans la top bar en V2 */
  readonly orgName = signal<string | null>(null);
  readonly orgLogoUrl = signal<string | null>(null);

  private loadOrgBrand(): void {
    const org = this.auth.currentOrg;
    this.orgName.set(org?.name ?? null);
    if (!org?.id) {
      this.orgLogoUrl.set(null);
      return;
    }
    this.organisationsService.getDetails(org.id).subscribe({
      next: (o) => this.orgLogoUrl.set(o.has_logo ? this.organisationsService.getLogoUrl(o.id, o.logo_updated_at) : null),
      error: () => this.orgLogoUrl.set(null),
    });
  }

  private sanitizeForLog(input: any): string {
    if (input == null) return 'null';
    const str = String(input);
    return str.replace(/[\r\n\t]/g, ' ').substring(0, 200);
  }




  private lastStatusEstId: string | null = null;
  private isLoadingStatus = false;

  private _watchSelectedEstablishment = effect(() => {
    const estId = this.session.selectedId();
    if (!estId) {
      this.lastStatusEstId = null;
      this.showWizardCta.set(false);
      this.showActionRequise.set(false);
      this.unreadMentionsCount.set(0);
      return;
    }
    if (this.lastStatusEstId === estId) return;
    this.refreshWizardCta(estId);
    // DEPRECATED (2026-09-08) : appel retiré avec l'alerte qualité -- voir la note en tête de
    // cette classe.
    // this.checkQualityAlert(estId);
    this.loadUnreadMentions(estId);
  }, { allowSignalWrites: true });

  ngOnInit(): void {
    if (this.initialized) return;
    this.initialized = true;

    this.maintenanceService.getBanner().subscribe(msg => this.maintenanceBanner.set(msg));
    // AuthService.bootstrap() a déjà hydraté la session au démarrage de l'app (rechargement de page) ;
    // on ne refait l'appel /auth/me ici que si ce n'est pas le cas (connexion/inscription fraîche).
    if (this.session.establishments().length === 0) {
      this.session.loadMe();
    }
    this.loadOrgBrand();
    setTimeout(() => {
      const id = this.session.selectedId();
      if (id && this.lastStatusEstId !== id) {
        this.refreshWizardCta(id);
      }
    }, 0);

    // Listen for wizard modal open events from other components
    window.addEventListener('openWizardModal', () => {
      this.openWizard();
    });

    // Listen for mentions refresh from actualite page
    window.addEventListener('refreshMentions', () => {
      const id = this.session.selectedId();
      if (id) this.loadUnreadMentions(id);
    });

    // Listen for organisation logo changes (upload/suppression depuis la page Site)
    window.addEventListener('organisationLogoUpdated', () => {
      this.loadOrgBrand();
    });
  }

  onEstablishmentChanged = (id: string) => {
    if (!id) return;
    this.session.selectEstablishment(id);
  };

  private async refreshWizardCta(id:string){
    if(this.isLoadingStatus&&this.lastStatusEstId===id)return;
    this.isLoadingStatus=true;this.lastStatusEstId=id;
    try{
      const s=await firstValueFrom(this.wizardApi.getEstablishmentStatus(id));
      if(!s){
        this.applyStatusToCta({wizardId:'establishment-setup',completedVersion:null,latestVersion:null,completed:false,completedAt:null,freezeUntil:null});
        return;
      }
      this.applyStatusToCta(s);
    }catch{
      this.showWizardCta.set(false);this.lastStatusEstId=null;
    }finally{
      this.isLoadingStatus=false;
    }
  }

  /** Logique basée sur completed/latest (aucune assignation de version par établissement n'existe côté backend) */
  private applyStatusToCta(s: WizardStatusDto): void {
    // Freeze = on cache
    if (s.freezeUntil && new Date(s.freezeUntil).getTime() > Date.now()) {
      this.showWizardCta.set(false);
      return;
    }

    const completed = s.completedVersion;
    const latest = s.latestVersion;

    // On propose la config seulement si rien n'a encore été fait
    if (completed == null && latest != null) {
      this.wizardLabel.set('Configurer site');
      this.showWizardCta.set(true);
    } else {
      this.showWizardCta.set(false);
    }
  }

  openWizard() {
    const estId = this.session.selectedId();
    if (!estId) return;
    this.showWizardModal.set(true);
    lockPageScroll();
  }
  closeWizard = () => {
    this.showWizardModal.set(false);
    unlockPageScroll();
    const id = this.session.selectedId();
    if (id) this.refreshWizardCta(id);
  };

  onWizardCompleted = () => {
    const id = this.session.selectedId();
    if (id) this.refreshWizardCta(id);

    // Rafraîchir la page Prévisions si elle est active
    if (this.router.url === '/previsions') {
      // Naviguer vers la même route pour déclencher le rafraîchissement
      this.router.navigateByUrl('/', { skipLocationChange: true }).then(() => {
        this.router.navigate(['/previsions']);
      });
    }
  };

  private checkQualityAlert(establishmentId: string) {
    this.satisfactionApi.getQualityAlert(establishmentId).subscribe({
      next: (alert) => {
        this.showActionRequise.set(alert.action_required);
      },
      error: () => {
        this.showActionRequise.set(false);
      }
    });
  }

  private loadUnreadMentions(establishmentId: string) {
    this.actualiteApi.getUnreadMentionsCount(establishmentId).subscribe({
      next: (response) => {
        this.unreadMentionsCount.set(response.count);
      },
      error: (err) => {
        // Ignorer silencieusement les erreurs 404/403 en attendant la mise à jour du backend
        if (err.status !== 403 && err.status !== 404) {
          console.error('Erreur chargement mentions:', this.sanitizeForLog(err));
        }
        this.unreadMentionsCount.set(0);
      }
    });
  }

  changeLang(code: string): void {
    this.languageService.setLang(code);
  }

  toggleAccountMenu() {
    this.showAccountMenu.set(!this.showAccountMenu());
  }

  closeAccountMenu() {
    this.showAccountMenu.set(false);
  }

  openAccount() {
    this.closeAccountMenu();
    this.showAccountModal.set(true);
    lockPageScroll();
    setTimeout(() => document.getElementById('acc-first')?.focus(), 0);
  }

  closeAccount() {
    this.showAccountModal.set(false);
    unlockPageScroll();
  }

  openAddUser() {
    this.closeAccountMenu();
    this.showAddUserModal.set(true);
    lockPageScroll();
  }

  openUserList() {
    this.closeAccountMenu();
    this.showUserListModal.set(true);
    lockPageScroll();
  }

  closeUserList() {
    this.showUserListModal.set(false);
    unlockPageScroll();
  }

  closeAddUser() {
    this.showAddUserModal.set(false);
    unlockPageScroll();
  }

  onUserAdded(user: any) {
    console.log('Nouvel utilisateur créé:', user);
    // Ici vous pouvez ajouter la logique pour rafraîchir la liste des utilisateurs
    // La modale ne se ferme plus automatiquement pour afficher le message de succès
  }

  async logout(): Promise<void> {
    await this.auth.logout();
    this.router.navigate(['/login']);
  }

  openSupport() {
    this.showSupportModal.set(true);
  }

  closeSupport() {
    this.showSupportModal.set(false);
  }

  openQrModal() {
    this.showQrModal.set(true);
  }

  closeQrModal() {
    this.showQrModal.set(false);
  }

  openPilotageFullscreen() {
    window.open('/pilotage-fullscreen', '_blank');
  }

  get selectedEstablishmentId(): string | null {
    return this.session.selectedId();
  }

  @HostListener('document:click')
  onDocumentClick() {
    this.closeAccountMenu();
  }




}
